import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { Carousel } from 'primereact/carousel';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { StyleClass } from "primereact/styleclass";
import { Ripple } from "primereact/ripple";

import ProductImage from './ProductImage';
import { buildImageUrl } from '../../lib/fileServer';
import { createProductUrl } from '../../utils/urlUtils';

const FeaturedProducts = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [filtroActivo, setFiltroActivo] = useState('estudio');
    const [productosDestacados, setProductosDestacados] = useState([
        { uso: 'gaming', equipos: [] },
        { uso: 'trabajo', equipos: [] },
        { uso: 'estudio', equipos: [] },
        { uso: 'edicion', equipos: [] }
    ]);
    const [productosPopulares, setProductosPopulares] = useState([]);
    const [cargandoPopulares, setCargandoPopulares] = useState(true);
    const navigate = useNavigate();
    const toast = useRef(null);
    const btnRef29 = useRef(null);

    // Variables de entorno
    const featuredLimit = parseInt(process.env.REACT_APP_FEATURED_PRODUCTS_LIMIT) || 4;
    const popularLimit = parseInt(process.env.REACT_APP_POPULAR_PRODUCTS_LIMIT) || 8;

    // Mapeo de conceptos a usos
    const conceptoAUso = {
        'gaming': 'gaming',
        'trabajo': 'trabajo',
        'estudios': 'estudio',
        'edicion': 'edicion'
    };

    // Función para cambiar el filtro
    const cambiarFiltro = (uso) => {
        setFiltroActivo(uso);
    };

    // Función para formatear importes
    const formatoImporte = (importe) => {
        return parseFloat(importe).toFixed(2);
    };

    const precioParteEntera = (precio) => {
        return formatoImporte(precio).split('.')[0];
    };

    const precioParteDecimal = (precio) => {
        return formatoImporte(precio).split('.')[1];
    };

    // Estado de carga
    const [cargando, setCargando] = useState(true);

    // Cargar productos destacados desde la API
    useEffect(() => {
        const cargarProductosDestacados = async () => {
            try {
                setCargando(true);
                
                // Verificar si estamos en desarrollo y el backend no está disponible
                const isDevelopment = process.env.NODE_ENV === 'development';
                
                // Intentar primero con el endpoint de Next.js (wins-next)
                let data;
                    try {
                        data = await apiService.get('/catalog/home');
                    } catch (expressError) {
                        console.error('Backend Express tampoco disponible:', expressError.message);
                        
                        // En desarrollo, usar datos mock
                        if (isDevelopment) {
                            console.warn('Usando datos mock para desarrollo');
                            const mockData = {
                                exito: true,
                                datos: {
                                    productos_destacados: [
                                        { id: 1, nombre: 'Laptop Gaming Mock', uso: 'gaming', precioventa: 2999, preciolista: 3299, url_foto: '/productos/12/fotos/1757382809489_xndlv.png' },
                                        { id: 2, nombre: 'Laptop Trabajo Mock', uso: 'trabajo', precioventa: 1999, preciolista: 2199, url_foto: '/productos/15/fotos/1757382809490_abcde.png' },
                                        { id: 3, nombre: 'Laptop Estudio Mock', uso: 'estudio', precioventa: 1499, preciolista: 1699, url_foto: '/productos/18/fotos/1757382809491_fghij.png' },
                                        { id: 4, nombre: 'Laptop Edición Mock', uso: 'edicion', precioventa: 3499, preciolista: 3799, url_foto: '/productos/21/fotos/1757382809492_klmno.png' }
                                    ]
                                }
                            };
                            
                            const productosConImagen = mockData.datos.productos_destacados.map(producto => ({
                                ...producto,
                                foto: buildImageUrl(producto.url_foto)
                            }));
                            
                            const productosAgrupados = [
                                { uso: 'gaming', equipos: productosConImagen.filter(p => p.uso === 'gaming').slice(0, featuredLimit) },
                                { uso: 'trabajo', equipos: productosConImagen.filter(p => p.uso === 'trabajo').slice(0, featuredLimit) },
                                { uso: 'estudio', equipos: productosConImagen.filter(p => p.uso === 'estudio').slice(0, featuredLimit) },
                                { uso: 'edicion', equipos: productosConImagen.filter(p => p.uso === 'edicion').slice(0, featuredLimit) }
                            ];
                            
                            setProductosDestacados(productosAgrupados);
                            return;
                        }
                        
                        throw expressError;
                    }
                

                //console.log(data.datos.productos_destacados)
                
                if (data.exito && data.datos && data.datos.productos_destacados) {
                    const productosDestacados = data.datos.productos_destacados;
                    
                    // Procesar cada categoría y agregar las imágenes
                    const productosAgrupados = [
                        { 
                            uso: 'gaming', 
                            equipos: (productosDestacados.gaming || []).map(producto => ({
                                ...producto,
                                foto: buildImageUrl(producto.foto_url || producto.foto)
                            })).slice(0, featuredLimit)
                        },
                        { 
                            uso: 'trabajo', 
                            equipos: (productosDestacados.trabajo || []).map(producto => ({
                                ...producto,
                                foto: buildImageUrl(producto.foto_url || producto.foto)
                            })).slice(0, featuredLimit)
                        },
                        { 
                            uso: 'estudio', 
                            equipos: (productosDestacados.estudios || []).map(producto => ({
                                ...producto,
                                foto: buildImageUrl(producto.foto_url || producto.foto)
                            })).slice(0, featuredLimit)
                        },
                        { 
                            uso: 'edicion', 
                            equipos: (productosDestacados.edicion || []).map(producto => ({
                                ...producto,
                                foto: buildImageUrl(producto.foto_url || producto.foto)
                            })).slice(0, featuredLimit)
                        }
                    ];
                    
                    setProductosDestacados(productosAgrupados);
                }
            } catch (error) {
                console.error('Error al cargar productos destacados:', error);
                
                // Mostrar mensaje de error más amigable
                if (error.message.includes('fetch')) {
                    console.error('Error de conexión: Verifique que el servidor backend esté ejecutándose');
                }
            } finally {
                setCargando(false);
            }
        };

        cargarProductosDestacados();
    }, [featuredLimit]);

    // Cargar productos populares desde la API
    useEffect(() => {
        const cargarProductosPopulares = async () => {
        try {
            setCargandoPopulares(true);
            
            // Verificar si estamos en desarrollo y el backend no está disponible
            const isDevelopment = process.env.NODE_ENV === 'development';
            
            // Intentar primero con el endpoint de Next.js (wins-next)
            let data;
                
                // Fallback al backend Express usando apiService
                try {
                    data = await apiService.get('/catalog/home');
                } catch (expressError) {
                    console.error('Backend Express tampoco disponible:', expressError.message);
                    
                    // En desarrollo, usar datos mock
                    if (isDevelopment) {
                        console.warn('Usando datos mock para productos populares');
                        const mockData = {
                            exito: true,
                            datos: {
                                productos_populares: [
                                    { id: 1, nombre: 'Laptop Popular 1', precioventa: 1999, preciolista: 2199, url_foto: '/productos/25/fotos/1757382809493_pqrst.png' },
                                    { id: 2, nombre: 'Laptop Popular 2', precioventa: 2499, preciolista: 2699, url_foto: '/productos/28/fotos/1757382809494_uvwxy.png' },
                                    { id: 3, nombre: 'Laptop Popular 3', precioventa: 1799, preciolista: 1999, url_foto: '/productos/31/fotos/1757382809495_zabcd.png' },
                                    { id: 4, nombre: 'Laptop Popular 4', precioventa: 2999, preciolista: 3299, url_foto: '/productos/34/fotos/1757382809496_efghi.png' }
                                ]
                            }
                        };
                        
                        const productosConImagen = mockData.datos.productos_populares.map(producto => ({
                            ...producto,
                            foto: buildImageUrl(producto.url_foto)
                        }));
                        
                        setProductosPopulares(productosConImagen.slice(0, popularLimit));
                        return;
                    }
                    
                    throw expressError;
                }
            
            
            if (data.exito && data.datos && data.datos.productos_populares) {
                const productosConImagen = data.datos.productos_populares.map(producto => ({
                    ...producto,
                    foto: buildImageUrl(producto.url_foto)
                }));
                
                setProductosPopulares(productosConImagen.slice(0, popularLimit));
            }
        } catch (error) {
            console.error('Error al cargar productos populares:', error);
            
            // Mostrar mensaje de error más amigable
            if (error.message.includes('fetch')) {
                console.error('Error de conexión: Verifique que el servidor backend esté ejecutándose');
            }
        } finally {
            setCargandoPopulares(false);
        }
    };

        cargarProductosPopulares();
    }, [popularLimit]);

    //console.log(productosDestacados);

    // Obtener productos filtrados
    const categoriaActual = productosDestacados.find(categoria => categoria.uso === filtroActivo);
    const equiposAMostrar = categoriaActual ? categoriaActual.equipos : [];

    const ProductoTarjetaTemplate = (producto) => {
        return (
            <div className="p-2 border-1 surface-border">
                <ProductImage 
                    producto={producto}
                    style={{ height: '300px', maxHeight: '300px' }}
                    className="w-full"
                />
                <div className="flex align-items-center justify-content-between mt-2 mb-2">
                    <span className="font-bold text-md">{producto.nombre}</span>
                </div>
                <div className='flex align-items-center justify-content-between mt-2 mb-2'>
                    {Number(producto.precioventa) < Number(producto.preciolista) && (
                        <div className="mr-auto">
                            <span className="font-bold text-xs p-overlay-badge w-full">Precio: S./ {formatoImporte(producto.preciolista)}</span><br/>
                            <span className="font-bold text-xs text-red-500">Ahorra: S./ {formatoImporte(Number(producto.preciolista) - Number(producto.precioventa))}</span>                            
                        </div>
                    )}
                    <div className="text-cyan-500 font-bold text-2xl lg:text-4xl text-right ml-auto">
                        <span className='text-xs lg:text-sm'>S./</span> {precioParteEntera(producto.precioventa)}
                        <span className='text-xs lg:text-sm'>.{precioParteDecimal(producto.precioventa)}</span>
                    </div>
                </div>
                <Button label="Ver Detalles..." outlined className='w-full' onClick={() => navigate(createProductUrl(producto.id, producto.nombre))} />
            </div>
        );
    };

    const ProductoPopularTemplate = (producto) => {
        return (
            <div className="p-2 border-1 surface-border">
                <ProductImage 
                    producto={producto}
                    style={{ height: '300px', maxHeight: '300px' }}
                    className="w-full"
                />
                <div className="flex align-items-center justify-content-between mt-2 mb-2">
                    <span className="font-bold text-sm">{producto.nombre}</span>
                </div>
                <div className='flex align-items-center justify-content-between mt-2 mb-2'>
                    {Number(producto.precioventa) < Number(producto.preciolista) && (
                        <div className="mr-auto">
                            <span className="font-bold text-xs p-overlay-badge">Precio: S./ {formatoImporte(producto.preciolista)}</span><br/>
                            <span className="font-bold text-xs text-red-500">Ahorra: S./ {formatoImporte(Number(producto.preciolista) - Number(producto.precioventa))}</span>                            
                        </div>
                    )}
                    <div className="text-cyan-500 font-bold text-xl lg:text-2xl text-right ml-auto">
                        <span className='text-xs'>S./</span> {precioParteEntera(producto.precioventa)}
                        <span className='text-xs'>.{precioParteDecimal(producto.precioventa)}</span>
                    </div>
                </div>
                <Button label="Ver Detalles..." outlined className='w-full' onClick={() => navigate(createProductUrl(producto.id, producto.nombre))} />
            </div>
        );
    };

    return (
        <div>
            <Toast ref={toast} />
            
            {/* Sección de Productos Destacados */}
            <div className="surface-section surface-border border-1 shadow-2 mb-4">
                <div className="surface-overlay shadow-2 flex relative lg:static justify-content-between" style={{ minHeight: '42px' }}>
                    <div className="flex flex-wrap lg:flex absolute lg:static w-full surface-overlay left-0 top-100 z-1 shadow-2 lg:shadow-none">
                        <span className="m-2 font-medium text-900 h-full vertical-align-middle text-lg font-bold hidden lg:block">EQUIPOS DESTACADOS</span>
                    </div>
                    <StyleClass nodeRef={btnRef29} selector="@next" enterClassName="hidden" leaveToClassName="hidden" hideOnOutsideClick>
                        <span className="m-2 font-medium text-900 h-full vertical-align-middle text-lg font-bold lg:hidden">EQUIPOS DESTACADOS</span>
                        <a ref={btnRef29} className="p-ripple cursor-pointer inline-flex align-items-center px-3 lg:hidden text-700">
                            <i className="pi pi-ellipsis-v text-xl"></i>
                        </a>
                    </StyleClass>
                    <div className="hidden lg:flex lg:justify-content-end absolute lg:static w-full surface-overlay left-0 top-100 z-1 shadow-2 lg:shadow-none">
                        <ul className="flex list-none p-0 m-0 flex-column lg:flex-row">
                            <li className="h-full border-left-1 surface-border">
                                <a 
                                    onClick={() => cambiarFiltro('estudio')} 
                                    className={`p-ripple cursor-pointer h-full inline-flex align-items-center text-600 border-left-2 lg:border-left-none lg:border-bottom-2 ${filtroActivo === 'estudio' ? 'border-blue-500' : 'border-transparent hover:border-500'} transition-colors transition-duration-150 py-3 lg:py-0 px-3`}
                                >
                                    <span className="pi pi-home mr-2"></span>
                                    <span className="font-medium">Estudios</span>
                                    <Ripple />
                                </a>
                            </li>
                            <li className="h-full">
                                <a 
                                    onClick={() => cambiarFiltro('trabajo')} 
                                    className={`p-ripple cursor-pointer h-full inline-flex align-items-center text-600 border-left-2 lg:border-left-none lg:border-bottom-2 ${filtroActivo === 'trabajo' ? 'border-blue-500' : 'border-transparent hover:border-500'} transition-colors transition-duration-150 py-3 lg:py-0 px-3`}
                                >
                                    <img src="/icons/catalog.svg" alt="Mouse icon" className="custom-icon mr-2" />
                                    <span className="font-medium">Trabajo</span>
                                    <Ripple />
                                </a>
                            </li>
                            <li className="h-full">
                                <a 
                                    onClick={() => cambiarFiltro('gaming')} 
                                    className={`p-ripple cursor-pointer h-full inline-flex align-items-center text-600 border-left-2 lg:border-left-none lg:border-bottom-2 ${filtroActivo === 'gaming' ? 'border-blue-500' : 'border-transparent hover:border-500'} transition-colors transition-duration-150 py-3 lg:py-0 px-3`}
                                >
                                    <span className="pi pi-tags mr-2"></span>
                                    <span className="font-medium">Gaming</span>
                                    <Ripple />
                                </a>
                            </li>
                            <li className="h-full">
                                <a 
                                    onClick={() => cambiarFiltro('edicion')} 
                                    className={`p-ripple cursor-pointer h-full inline-flex align-items-center text-600 border-left-2 lg:border-left-none lg:border-bottom-2 ${filtroActivo === 'edicion' ? 'border-blue-500' : 'border-transparent hover:border-500'} transition-colors transition-duration-150 py-3 lg:py-0 px-3`}
                                >
                                    <span className="pi pi-truck mr-2"></span>
                                    <span className="font-medium">Edición & Diseño</span>
                                    <Ripple />
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="grid m-2">
                    {cargando ? (
                        // Mostrar skeletons mientras carga
                        Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className="col-12 md:col-6 lg:col-3">
                                <div className="p-2 border-1 surface-border">
                                    <Skeleton height="300px" className="mb-2" />
                                    <Skeleton width="100%" height="1rem" className="mb-2" />
                                    <Skeleton width="75%" height="1rem" className="mb-2" />
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                            </div>
                        ))
                    ) : equiposAMostrar.length > 0 ? (
                        // Mostrar productos si existen
                        equiposAMostrar.map((equipo, index) => (
                            <div key={index} className="col-12 md:col-6 lg:col-3">
                                {ProductoTarjetaTemplate(equipo)}
                            </div>
                        ))
                    ) : (
                        // Mostrar mensaje si no hay productos
                        <div className="col-12">
                            <div className="text-center p-4">
                                <i className="pi pi-info-circle text-4xl text-gray-400 mb-3"></i>
                                <p className="text-gray-600">No hay productos disponibles para esta categoría</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sección de Productos Populares */}
            <div className="surface-section surface-border border-1 shadow-2">
                <div className="surface-overlay shadow-2 flex relative lg:static justify-content-between" style={{ minHeight: '42px' }}>
                    <div className="flex flex-wrap lg:flex absolute lg:static w-full surface-overlay left-0 top-100 z-1 shadow-2 lg:shadow-none">
                        <span className="m-2 font-medium text-900 h-full vertical-align-middle text-lg font-bold">PRODUCTOS POPULARES</span>
                    </div>
                </div>

                {cargandoPopulares ? (
                    <div className="grid m-2">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className="col-12 md:col-6 lg:col-3">
                                <div className="p-2 border-1 surface-border">
                                    <Skeleton height="200px" className="mb-2" />
                                    <Skeleton width="100%" height="1rem" className="mb-2" />
                                    <Skeleton width="75%" height="1rem" className="mb-2" />
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="hidden lg:block">
                        <Carousel 
                            value={productosPopulares} 
                            numVisible={4} 
                            numScroll={1} 
                            itemTemplate={ProductoPopularTemplate}
                            responsiveOptions={[
                                { breakpoint: '1024px', numVisible: 3, numScroll: 1 },
                                { breakpoint: '768px', numVisible: 2, numScroll: 1 },
                                { breakpoint: '560px', numVisible: 1, numScroll: 1 }
                            ]}
                            className="m-2"
                        />
                    </div>
                )}

                <div className="lg:hidden">
                    <div className="grid m-2">
                        {productosPopulares.slice(0, 4).map((producto, index) => (
                            <div key={index} className="col-12 md:col-6">
                                {ProductoPopularTemplate(producto)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeaturedProducts;
